import { useRouter } from 'next/navigation';
import { cx } from '@/utils';
import DotButton, { useDotButton } from '@common/DotButton/DotButton';
import { t } from '@lingui/macro';
import { TitleWrapper } from '@views/Home/components';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';
import useEmblaCarousel from 'embla-carousel-react';

import { ROUTE_PARTICIPATE, ROUTES_CLIENT, ROUTES_INTERNAL } from '@/utils/constants';
import { formatDate } from '@/utils/dateHelpers';
import { PageRegulationsBlock } from '@/components/common/Providers/GraphqlProvider/generated';

import CardDownloadParticipate from '../../../views/Participate/components/CardDownloadParticipate';
import styles from './styles.module.css';

const LastUpdate = ({
  currentPageRegulationsBlock,
}: {
  currentPageRegulationsBlock?: PageRegulationsBlock;
}) => {
  const {
    home: { regulationsBlock: regulationsBlockContext },
  } = useHomeContext();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 420px)': { slidesToScroll: 1 },
      '(min-width: 768px)': { slidesToScroll: 4 },
    },
    loop: false,
  });

  const regulationsBlock = currentPageRegulationsBlock || regulationsBlockContext;

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);
  const router = useRouter();
  if (regulationsBlock?.isDisabled) return null;

  return (
    <section className={styles.wrapper}>
      <div className={'inner-extend'}>
        <TitleWrapper
          title1={
            !regulationsBlock?.showTitle ? '' : regulationsBlock?.titleRegulation?.split(' ')[0]
          }
          title2={
            !regulationsBlock?.showTitle
              ? ''
              : regulationsBlock?.titleRegulation?.split(' ').slice(1).join(' ')
          }
          description={
            !regulationsBlock?.showDescription ? '' : regulationsBlock?.descriptionRegulation
          }
          action={{ text: 'Ver mas', onClick: () => router.push('/' + ROUTES_CLIENT.REGULATIONS) }}
        />
        <div className=" overflow-hidden px-[1rem] pb-2 md:mx-0 md:px-0" ref={emblaRef}>
          <div className={cx(styles.content)}>
            {regulationsBlock?.regulations?.map((regulation) => (
              <div className="min-w-0 flex-[0_0_80%] md:flex-[0_0_24%]" key={regulation.id}>
                <CardDownloadParticipate
                  title={regulation.title}
                  publicationDate={regulation.date}
                  expeditionDate={formatDate(regulation.documentDate, 'DD/MM/YYYY')}
                  style={regulationsBlock.pageBlockStyle}
                  description={regulation.description}
                  downloadText={regulation.downloadText}
                  image={regulation.image?.id}
                  downloadLink={regulation.linkItem?.url}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={'dots mt-8 block md:hidden'}>
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={cx('dot', {
                [styles.selected]: index === selectedIndex,
              })}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LastUpdate;

export function LastUpdateSkeleton() {
  return (
    <section className={styles.wrapper}>
      <div className="inner-extend">
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-48 rounded bg-gray-200" />
          <div className="space-y-3">
            <div className="h-6 w-full rounded bg-gray-200" />
            <div className="h-6 w-full rounded bg-gray-200" />
            <div className="h-6 w-full rounded bg-gray-200" />
          </div>
        </div>
      </div>
    </section>
  );
}
